/**
 * hooks/useMailcheck.ts
 *
 * Client hook — Mailcheck v1.1.1 (MIT) typo-suggestion widget.
 * Ported from _astro-legacy/src/pages/contact.astro lines 345–392.
 * No npm dependency; algorithm is inlined here (same as legacy).
 *
 * Usage:
 *   const { runMailcheck, checkOnSubmit } = useMailcheck({
 *     emailInputId: 'contact-email',
 *     suggestionDivId: 'contact-email-suggestion',
 *   });
 *
 *   // Wire onto the email input's input (debounced) AND blur events.
 *   // Call checkOnSubmit() from the form's submit handler.
 *   //   Returns true  → suggestion shown, submit should be intercepted once.
 *   //   Returns false → no new suggestion, submit can proceed.
 *
 * Behaviour:
 *   - On input (debounced 400ms): if the value passes EMAIL_COMPLETE_RE, runs
 *     Mailcheck and shows suggestion if found. iOS Safari fires `input` events
 *     correctly during typing; this is the primary trigger path.
 *   - On blur: also runs check (backup — catches paste, autofill completion).
 *   - Submit-gate (Layer 1b): on submit, if a suggestion is currently visible
 *     (user has not dismissed/accepted), returns true to signal the caller to
 *     intercept this submit attempt. Second submit proceeds regardless.
 *   - aria-live="polite" is expected on the suggestion div (already in the markup).
 *   - dismiss link: role="button" + tabindex="0" + Enter/Space activation.
 *   - Cleanup on unmount (listeners removed, suggestion cleared).
 *
 * Configuration: trimmed domain list covering ~99% of Piano Fantasy audience
 * (consumer + Western-European). Dropped: gmx.*, arcor, freenet, t-online, web.de,
 * hetnet, casema, kpnmail, planet, xs4all, zonnet, mail.ru, yandex, googlemail.
 * Brief: 2026-05-08 continuation cycle Bug 2 fix.
 * Thresholds: domainThreshold:2, secondLevelThreshold:2, topLevelThreshold:2.
 *
 * Cycle 2026-05-08 — Deliverable 1 (FB-mailcheck-restore).
 * Cycle 2026-05-08 — Continuation: Bug 1 (input-debounce), Bug 2 (dict trim), Bug 3 (CSS).
 * Cycle 2026-05-11 — Bug 4 fix (TLD-only typo, e.g. gmail.co → gmail.com):
 *   The early-return "both parts in known lists → no suggestion" shortcut was firing
 *   before the full-domain close-match check for inputs like lars@gmail.co.
 *   'gmail' ∈ MAILCHECK_SECOND_LEVEL AND 'co' ∈ MAILCHECK_TLD → early return fired,
 *   but 'gmail.co' is NOT in MAILCHECK_DOMAINS → the shortcut was wrong.
 *   Fix: add MAILCHECK_DOMAINS.includes(split.domain) guard to the early-return condition.
 *   Also adds pm.me + pm to MAILCHECK_DOMAINS / MAILCHECK_SECOND_LEVEL respectively.
 */

'use client';

import { useEffect, useRef } from 'react';

// ─── Mailcheck v1.1.1 algorithm (inlined, MIT licence) ──────────────────────
// Source: contact.astro line 346 (minified original); formatted here for
// readability and TypeScript compliance. No behavioural changes.

interface MailcheckResult {
  address: string;
  domain: string;
  full: string;
}

// Domain dictionary trimmed to ~99% consumer coverage for Piano Fantasy audience
// (US + Western Europe). Removed: arcor.de, freenet.de, t-online.de, web.de,
// hetnet.nl, casema.nl, kpnmail.nl, planet.nl, xs4all.nl, zonnet.nl, home.nl,
// mail.ru, yandex.com/ru, googlemail.com.
//
// gmx.com is kept in MAILCHECK_DOMAINS (but NOT in MAILCHECK_SECOND_LEVEL):
//   - Full-domain exact match (lars@gmx.com → gmx.com === gmx.com) triggers
//     the early-return false path → no suggestion shown. Correct.
//   - Absent from MAILCHECK_SECOND_LEVEL so "gmx" never surfaces as a
//     second-level close-match for typos → no noise for inputs like hmal.co.
// This is the "exact-match no-suggest" pattern from the brief.
// Brief: 2026-05-08 continuation Bug 2.
const MAILCHECK_DOMAINS = [
  'aol.com','aol.co.uk',
  'fastmail.com','fastmail.fm',
  'gmail.com',
  'gmx.com',   /* exact-match no-suggest only — not in MAILCHECK_SECOND_LEVEL */
  'hotmail.com','hotmail.co.uk','hotmail.de','hotmail.fr','hotmail.it','hotmail.nl',
  'icloud.com',
  'live.com','live.nl',
  'mac.com','me.com','msn.com',
  'outlook.com','outlook.de','outlook.fr','outlook.it','outlook.nl',
  'pm.me',     /* Proton short domain — exact-match no-suggest; also catches pm.co → pm.me */
  'proton.me','protonmail.com',
  'yahoo.com','yahoo.co.uk','yahoo.de','yahoo.fr','yahoo.it','yahoo.nl',
  'ziggo.nl',
];
const MAILCHECK_SECOND_LEVEL = [
  'aol','fastmail','gmail',
  'hotmail','icloud','live','mac','me','msn',
  'outlook','pm','proton','protonmail',
  'yahoo','ziggo',
];
const MAILCHECK_TLD = [
  'com','net','org','co','co.uk','nl','de','fr','it','es','eu','be','lu','ch',
  'at','dk','se','no','fi','ie','com.au','co.nz','ca',
];
const EMAIL_COMPLETE_RE = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;

function sift3Distance(a: string, b: string): number {
  if (!a || a.length === 0) return !b || b.length === 0 ? 0 : b.length;
  if (!b || b.length === 0) return a.length;
  let c = 0, d = 0, e = 0, f = 0;
  const g = 5;
  while (c + d < a.length && c + e < b.length) {
    if (a.charAt(c + d) === b.charAt(c + e)) {
      f++;
    } else {
      d = 0; e = 0;
      for (let h = 0; h < g; h++) {
        if (c + h < a.length && a.charAt(c + h) === b.charAt(c)) { d = h; break; }
        if (c + h < b.length && a.charAt(c) === b.charAt(c + h)) { e = h; break; }
      }
    }
    c++;
  }
  return (a.length + b.length) / 2 - f;
}

function splitEmail(email: string): false | { topLevelDomain: string; secondLevelDomain: string; domain: string; address: string } {
  const parts = email.trim().split('@');
  if (parts.length < 2) return false;
  for (const p of parts) if (p === '') return false;
  const domain = parts.pop()!;
  const domainParts = domain.split('.');
  if (domainParts.length === 0) return false;
  let secondLevelDomain = '';
  let topLevelDomain = '';
  if (domainParts.length === 1) {
    topLevelDomain = domainParts[0];
  } else {
    secondLevelDomain = domainParts[0];
    let tld = '';
    for (let i = 1; i < domainParts.length; i++) tld += domainParts[i] + '.';
    topLevelDomain = tld.substring(0, tld.length - 1);
  }
  return { topLevelDomain, secondLevelDomain, domain, address: parts.join('@') };
}

function findClosestDomain(str: string, domains: string[], distFn: typeof sift3Distance, threshold: number): string | false {
  if (!str || !domains) return false;
  let minDist = 99;
  let closest: string | null = null;
  for (const d of domains) {
    if (str === d) return str;
    const dist = distFn(str, d);
    if (dist < minDist) { minDist = dist; closest = d; }
  }
  return threshold >= minDist && closest !== null ? closest : false;
}

function mailcheckSuggest(email: string): MailcheckResult | false {
  const lower = email.toLowerCase();
  const split = splitEmail(lower);
  if (!split) return false;

  // If the second-level and TLD are both in our known lists, AND the full domain is a
  // confirmed known-correct domain, it is probably correct — skip close-match checks.
  //
  // Bug 4 fix (2026-05-11): the previous guard did not include the full-domain check.
  // 'gmail.co' has secondLevel='gmail' (∈ list) + TLD='co' (∈ list), so the early-return
  // fired and swallowed the suggestion. But 'gmail.co' is NOT in MAILCHECK_DOMAINS, so
  // the full-domain close-match should run and catch the .co → .com TLD typo.
  // The new guard requires ALL THREE conditions before short-circuiting:
  //   1. secondLevelDomain is in MAILCHECK_SECOND_LEVEL
  //   2. topLevelDomain is in MAILCHECK_TLD
  //   3. the full domain is in MAILCHECK_DOMAINS (i.e. it is an actual known-valid domain)
  if (
    MAILCHECK_SECOND_LEVEL.indexOf(split.secondLevelDomain) !== -1 &&
    MAILCHECK_TLD.indexOf(split.topLevelDomain) !== -1 &&
    MAILCHECK_DOMAINS.indexOf(split.domain) !== -1
  ) return false;

  const closestDomain = findClosestDomain(split.domain, MAILCHECK_DOMAINS, sift3Distance, 2);
  if (closestDomain) {
    if (closestDomain === split.domain) return false;
    return { address: split.address, domain: closestDomain, full: split.address + '@' + closestDomain };
  }

  const closestSecond = findClosestDomain(split.secondLevelDomain, MAILCHECK_SECOND_LEVEL, sift3Distance, 2);
  const closestTld    = findClosestDomain(split.topLevelDomain,    MAILCHECK_TLD,          sift3Distance, 2);

  if (split.domain) {
    let newDomain = split.domain;
    let changed = false;
    if (closestSecond && closestSecond !== split.secondLevelDomain) {
      newDomain = newDomain.replace(split.secondLevelDomain, closestSecond);
      changed = true;
    }
    if (closestTld && closestTld !== split.topLevelDomain) {
      newDomain = newDomain.replace(split.topLevelDomain, closestTld);
      changed = true;
    }
    if (changed) return { address: split.address, domain: newDomain, full: split.address + '@' + newDomain };
  }
  return false;
}

// ─── Cycle 70: Format-hint layer ─────────────────────────────────────────────
// Runs BEFORE mailcheckSuggest() and BEFORE the EMAIL_COMPLETE_RE guard.
// Catches malformed inputs that the regex would gate out:
//   Rule 1: missing @ (testgmail.com, testgmailcom)
//   Rule 2: no dot in domain part (test@gmail → test@gmail.com via shorthand)
//   Rule 3: space in domain part (test@gmail com → test@gmail.com)
//   Rule 4: multiple @ signs (test@@gmail.com → test@gmail.com)
//
// All four edge-case inputs Lars tested on real mobile now get actionable hints.
// When a format issue is detected, runCheck() shows the hint and returns early —
// mailcheckSuggest() is only reached on syntactically valid emails.

const DOMAIN_SHORTHAND: Record<string, string> = {
  gmail: 'gmail.com', hotmail: 'hotmail.com', outlook: 'outlook.com',
  icloud: 'icloud.com', yahoo: 'yahoo.com', proton: 'proton.me',
  protonmail: 'protonmail.com', live: 'live.com', ziggo: 'ziggo.nl',
  me: 'me.com', aol: 'aol.com', msn: 'msn.com', mac: 'mac.com',
  fastmail: 'fastmail.com', pm: 'pm.me',
};

interface FormatHint {
  message: string;
  suggestion: string | null; // non-null → use mcShow(); null → plain text
}

function checkFormatHint(raw: string): FormatHint | null {
  const v = raw.trim();
  if (!v) return null;

  const atCount = (v.match(/@/g) || []).length;

  // Rule 4: multiple @ signs — collapse to single
  if (atCount > 1) {
    const parts = v.split('@');
    const local = parts[0];
    const rest  = parts.slice(1).join('').replace(/\s+/g, '');
    const suggestion = local + '@' + rest;
    return { message: '', suggestion };
  }

  // Rule 1: no @ at all — try to find a known domain substring
  if (atCount === 0) {
    // Look for a known shorthand key embedded in the value (e.g. "testgmail")
    for (const [key, full] of Object.entries(DOMAIN_SHORTHAND)) {
      // match "testgmail.com" or "testgmailcom" or "testgmail"
      const dotIdx = v.indexOf('.');
      const base = dotIdx !== -1 ? v.substring(0, dotIdx) : v;
      const idx = base.toLowerCase().indexOf(key);
      if (idx > 0) {
        const local = base.substring(0, idx);
        return { message: '', suggestion: local + '@' + full };
      }
    }
    return { message: 'Please include an @ in your email address.', suggestion: null };
  }

  const [local, domain] = v.split('@');

  // Rule 3: space in domain part
  if (domain && /\s/.test(domain)) {
    const fixedDomain = domain.replace(/\s+/g, '.');
    const suggestion  = local + '@' + fixedDomain;
    return { message: '', suggestion };
  }

  // Rule 2: no dot in domain → look up shorthand, fall back to appending .com
  if (domain && !domain.includes('.')) {
    const full = DOMAIN_SHORTHAND[domain.toLowerCase()] ?? (domain + '.com');
    return { message: '', suggestion: local + '@' + full };
  }

  return null; // no format issue detected — let normal mailcheckSuggest run
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export interface UseMailcheckOptions {
  /** ID of the email <input> element */
  emailInputId: string;
  /** ID of the .mailcheck-suggestion <div> */
  suggestionDivId: string;
}

export interface UseMailcheckReturn {
  /**
   * Call from a form submit handler (before any other logic).
   * Returns true if a NEW suggestion was shown — caller should stop the submit,
   * let the user acknowledge, and allow re-submit to proceed.
   * Returns false if no new suggestion (submit may continue).
   *
   * The gate fires at most ONCE per continuous suggestion: after the user
   * dismisses/accepts and re-submits, checkOnSubmit() returns false.
   */
  checkOnSubmit: () => boolean;
}

export function useMailcheck({ emailInputId, suggestionDivId }: UseMailcheckOptions): UseMailcheckReturn {
  // Tracks whether the current suggestion has already intercepted one submit.
  // Reset to false when suggestion is cleared.
  const submitInterceptedRef = useRef<boolean>(false);

  // Expose checkOnSubmit to consumers via a stable ref so the callback identity
  // doesn't change across renders and the consumer's useEffect doesn't re-run.
  const checkOnSubmitRef = useRef<() => boolean>(() => false);

  useEffect(() => {
    const inputEl = document.getElementById(emailInputId) as HTMLInputElement | null;
    const slotEl  = document.getElementById(suggestionDivId) as HTMLDivElement | null;
    if (!inputEl || !slotEl) return;

    function mcHide() {
      slotEl!.classList.remove('is-visible');
      setTimeout(() => {
        if (!slotEl!.classList.contains('is-visible')) slotEl!.innerHTML = '';
      }, 240);
      submitInterceptedRef.current = false;
    }

    function mcShow(suggested: string) {
      slotEl!.innerHTML = '';

      const text = document.createTextNode('Did you mean ');
      const link = document.createElement('a');
      link.textContent = suggested;
      link.href = '#';
      link.setAttribute('role', 'button');
      link.addEventListener('click', (e) => {
        e.preventDefault();
        inputEl!.value = suggested;
        mcHide();
        inputEl!.focus();
      });

      const dismiss = document.createElement('span');
      dismiss.textContent = '× that’s correct';
      dismiss.className = 'mailcheck-dismiss';
      dismiss.setAttribute('role', 'button');
      dismiss.setAttribute('tabindex', '0');
      dismiss.addEventListener('click', () => mcHide());
      dismiss.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); mcHide(); }
      });

      slotEl!.appendChild(text);
      slotEl!.appendChild(link);
      slotEl!.appendChild(document.createTextNode('?  '));
      slotEl!.appendChild(dismiss);
      slotEl!.classList.add('is-visible');
    }

    function runCheck(value: string): boolean {
      if (!value) { mcHide(); return false; }

      // Cycle 70: format-hint layer — runs before EMAIL_COMPLETE_RE guard
      // so malformed inputs (missing @, missing TLD, space, double-@) get
      // actionable suggestions instead of silently showing nothing.
      if (!EMAIL_COMPLETE_RE.test(value)) {
        const hint = checkFormatHint(value);
        if (hint) {
          if (hint.suggestion) {
            mcShow(hint.suggestion);
          } else {
            slotEl!.innerHTML = '';
            slotEl!.appendChild(document.createTextNode(hint.message));
            slotEl!.classList.add('is-visible');
          }
          return true;
        }
        mcHide();
        return false;
      }
      const result = mailcheckSuggest(value);
      if (result) {
        // Defense-in-depth: the suggestion MUST preserve the typed local part.
        // mailcheckSuggest()'s two return paths both use split.address (= everything
        // before @ in the input), so this can never fail under normal operation.
        // This assertion guards against any future dictionary/algorithm change that
        // might inadvertently substitute the local part — the root cause of the
        // larsnelissen@gmx.com report (2026-05-08: that string came from iOS autofill
        // keychain, not from this hook; the hook is provably incapable of substituting
        // the local part — see investigation note below).
        //
        // NOTE — root-cause analysis (2026-05-08 continuation cycle):
        //   Lars reported seeing 'larsnelissen@gmx.com' when he typed 'lars@hmal.co'.
        //   grep -rin 'larsnelissen' across the entire repo finds only Facebook profile URLs.
        //   The hook's only suggestion-producing code paths are lines:
        //     { address: split.address, ..., full: split.address + '@' + closestDomain }
        //   split.address = parts.join('@') from splitEmail() = everything before the last @
        //   in the typed value. For 'lars@hmal.co', split.address = 'lars'. Period.
        //   Simulation confirmed: neither the d370fcc nor a4f3523 hook version can produce
        //   a suggestion with 'larsnelissen' as the local part from 'lars@hmal.co' input.
        //   Conclusion: the 'larsnelissen@gmx.com' text appeared in the iOS keyboard
        //   autofill bar (iCloud Keychain suggestion above the keyboard), NOT in this
        //   hook's suggestion div. They are visually similar on iPhone — both appear
        //   near the email input. The hook's div was either empty or hidden at that moment.
        const typedLocalPart = value.split('@')[0];
        if (!result.full.startsWith(typedLocalPart + '@')) {
          // Local part mismatch — refuse to show this suggestion.
          mcHide();
          return false;
        }
        mcShow(result.full);
        return true;
      } else {
        mcHide();
        return false;
      }
    }

    // input (debounced 400ms): primary trigger on iOS Safari and all browsers.
    // iOS Safari fires `input` events correctly during typing, making this the
    // most reliable real-time path. 400ms debounce avoids thrashing on every
    // keystroke while keeping the suggestion responsive after a typing pause.
    // Bug 1 fix — 2026-05-08 continuation cycle.
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const onInput = () => {
      if (debounceTimer !== null) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        runCheck(inputEl.value.trim());
      }, 400);
    };
    inputEl.addEventListener('input', onInput);

    // blur: backup trigger — catches paste, autofill completion, and cases
    // where input events are suppressed. Clears the debounce timer first so
    // blur and debounce don't race.
    const onBlur = () => {
      if (debounceTimer !== null) { clearTimeout(debounceTimer); debounceTimer = null; }
      runCheck(inputEl.value.trim());
    };
    inputEl.addEventListener('blur', onBlur);

    // Expose checkOnSubmit via ref — stable across renders
    checkOnSubmitRef.current = () => {
      const value = inputEl.value.trim();
      const suggestionVisible = slotEl.classList.contains('is-visible');

      // If a suggestion is already visible and we haven't yet intercepted this one:
      if (suggestionVisible && !submitInterceptedRef.current) {
        submitInterceptedRef.current = true;
        return true; // intercept — caller should stop the submit this time
      }

      // If no suggestion is visible, run a final check on submit
      if (!suggestionVisible) {
        const fired = runCheck(value);
        if (fired && !submitInterceptedRef.current) {
          submitInterceptedRef.current = true;
          return true; // new suggestion shown — intercept once
        }
      }

      return false; // no intercept — proceed with submit
    };

    return () => {
      if (debounceTimer !== null) clearTimeout(debounceTimer);
      inputEl.removeEventListener('input', onInput);
      inputEl.removeEventListener('blur', onBlur);
      checkOnSubmitRef.current = () => false;
    };
  }, [emailInputId, suggestionDivId]);

  return {
    checkOnSubmit: () => checkOnSubmitRef.current(),
  };
}
